/**
 * interactiveDirectEdit.ts
 * Utility to enable direct inline text editing AND direct drag-and-drop element positioning
 * on rendered template HTML preview sheets, WITH full persistence to LocalStorage!
 */

export const LS_DIRECT_EDITS_KEY = "mini_printer_direct_edits_v1";

export interface ElementEditRecord {
  selector: string;
  text?: string;
  translateX?: number;
  translateY?: number;
}

export type DirectEditStore = Record<string, ElementEditRecord[]>;

export function getSavedDirectEdits(): DirectEditStore {
  try {
    const json = localStorage.getItem(LS_DIRECT_EDITS_KEY);
    return json ? JSON.parse(json) : {};
  } catch {
    return {};
  }
}

export function saveDirectEditsToLS(store: DirectEditStore): void {
  try {
    localStorage.setItem(LS_DIRECT_EDITS_KEY, JSON.stringify(store));
  } catch {
    // ignore
  }
}

function getElementUniqueKey(el: HTMLElement, index: number): string {
  if (el.dataset.fieldId) return `field-id:${el.dataset.fieldId}`;
  if (el.dataset.fieldKey) return `field-key:${el.dataset.fieldKey}`;
  if (el.id) return `#${el.id}`;

  // Unique fallback selector based on tag name and index
  return `${el.tagName.toLowerCase()}-${index}`;
}

export function saveContainerDirectEdits(container: HTMLElement, templateId: string): void {
  if (!container || !templateId) return;

  const targetElements = container.querySelectorAll<HTMLElement>(
    "span, p, td, th, h1, h2, h3, h4, table, div.field-value, div.field-label, div.field-row, [data-field-id], .template-element"
  );

  const records: ElementEditRecord[] = [];

  targetElements.forEach((el, index) => {
    const key = getElementUniqueKey(el, index);
    const style = window.getComputedStyle(el);
    const matrix = new WebKitCSSMatrix(style.transform);
    const translateX = matrix.m41;
    const translateY = matrix.m42;
    const isEditedText = el.hasAttribute("data-user-edited-text");
    const text = isEditedText ? el.innerText : undefined;

    if (translateX !== 0 || translateY !== 0 || text !== undefined) {
      records.push({
        selector: key,
        text,
        translateX: translateX !== 0 ? translateX : undefined,
        translateY: translateY !== 0 ? translateY : undefined,
      });
    }
  });

  const store = getSavedDirectEdits();
  store[templateId] = records;
  saveDirectEditsToLS(store);
}

export function applySavedContainerDirectEdits(container: HTMLElement, templateId: string): void {
  if (!container || !templateId) return;

  const store = getSavedDirectEdits();
  const records = store[templateId];
  if (!records || records.length === 0) return;

  const targetElements = container.querySelectorAll<HTMLElement>(
    "span, p, td, th, h1, h2, h3, h4, table, div.field-value, div.field-label, div.field-row, [data-field-id], .template-element"
  );

  const recordMap = new Map<string, ElementEditRecord>();
  records.forEach((r) => recordMap.set(r.selector, r));

  targetElements.forEach((el, index) => {
    const key = getElementUniqueKey(el, index);
    const record = recordMap.get(key);
    if (!record) return;

    if (record.text !== undefined) {
      el.innerText = record.text;
      el.setAttribute("data-user-edited-text", "true");
    }

    if (record.translateX !== undefined || record.translateY !== undefined) {
      const tx = record.translateX ?? 0;
      const ty = record.translateY ?? 0;
      el.style.position = el.style.position || "relative";
      el.style.transform = `translate(${tx}px, ${ty}px)`;
    }
  });
}

export function setupInteractiveDirectEdit(
  container: HTMLElement,
  enabled: boolean,
  templateId?: string,
  options?: {
    onChanged?: () => void;
    /** When false, the native mousedown drag is skipped (e.g. when a drag library is used instead). Default true. */
    drag?: boolean;
  }
): () => void {
  if (!container) return () => {};

  const enableDrag = options?.drag ?? true;

  // Reapply saved edits first if templateId provided
  if (templateId) {
    applySavedContainerDirectEdits(container, templateId);
  }

  const targetElements = container.querySelectorAll<HTMLElement>(
    "span, p, td, th, h1, h2, h3, h4, table, div.field-value, div.field-label, div.field-row, [data-field-id], .template-element"
  );

  const cleanupFns: Array<() => void> = [];

  if (!enabled) {
    targetElements.forEach((el) => {
      el.removeAttribute("contenteditable");
      el.removeAttribute("draggable");
      el.removeAttribute("data-editable-setup");
      el.style.cursor = "";
      el.style.userSelect = "";
      el.classList.remove(
        "ring-1",
        "ring-amber-400/80",
        "hover:bg-amber-50/90",
        "focus:bg-amber-100",
        "focus:outline-none",
        "focus:ring-2",
        "focus:ring-amber-500",
        "cursor-text",
        "cursor-grab",
        "active:cursor-grabbing",
        "rounded-xs"
      );
    });
    return () => {};
  }

  const EDITABLE_SELECTOR =
    "span, p, td, th, h1, h2, h3, h4, table, div.field-value, div.field-label, div.field-row, [data-field-id], .template-element";

  /**
   * Apply the editable + drag setup to one element. Idempotent via a data
   * marker so re-running over an already-set-up element doesn't double-bind the
   * input/mousedown listeners.
   */
  const setupElement = (el: HTMLElement) => {
    if (el.hasAttribute("data-editable-setup")) return;
    el.setAttribute("data-editable-setup", "true");

    const isLeaf =
      el.children.length === 0 ||
      Array.from(el.children).every((c) =>
        ["BR", "B", "SPAN", "STRONG", "EM", "I", "SMALL"].includes(c.tagName)
      );

    if (isLeaf) {
      el.setAttribute("contenteditable", "true");
      el.classList.add(
        "ring-1",
        "ring-amber-400/80",
        "hover:bg-amber-50/90",
        "focus:bg-amber-100",
        "focus:outline-none",
        "focus:ring-2",
        "focus:ring-amber-500",
        "rounded-xs"
      );

      const handleInput = () => {
        el.setAttribute("data-user-edited-text", "true");
        if (templateId) {
          saveContainerDirectEdits(container, templateId);
        }
        options?.onChanged?.();
      };
      el.addEventListener("input", handleInput);
      cleanupFns.push(() => el.removeEventListener("input", handleInput));
    }

    if (enableDrag && (el.tagName === "DIV" || el.tagName === "P" || el.tagName === "TABLE" || el.dataset.fieldId)) {
      el.style.position = el.style.position || "relative";
      el.style.cursor = "grab";

      let isDragging = false;
      let startX = 0;
      let startY = 0;
      let initialTranslateX = 0;
      let initialTranslateY = 0;

      const getTranslate = () => {
        const style = window.getComputedStyle(el);
        const matrix = new WebKitCSSMatrix(style.transform);
        return { x: matrix.m41, y: matrix.m42 };
      };

      const handleMouseDown = (e: MouseEvent) => {
        if (document.activeElement === el && !e.shiftKey) return;

        isDragging = true;
        let didDrag = false;
        startX = e.clientX;
        startY = e.clientY;
        const currentTrans = getTranslate();
        initialTranslateX = currentTrans.x;
        initialTranslateY = currentTrans.y;

        el.style.cursor = "grabbing";
        el.classList.add("ring-2", "ring-blue-500", "shadow-md");

        const handleMouseMove = (moveEvt: MouseEvent) => {
          if (!isDragging) return;
          const dx = moveEvt.clientX - startX;
          const dy = moveEvt.clientY - startY;
          if (!didDrag && Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
          didDrag = true;
          if (moveEvt.cancelable) moveEvt.preventDefault();
          el.style.transform = `translate(${initialTranslateX + dx}px, ${initialTranslateY + dy}px)`;
        };

        const handleMouseUp = () => {
          if (isDragging) {
            isDragging = false;
            el.style.cursor = "grab";
            el.classList.remove("ring-2", "ring-blue-500", "shadow-md");
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);

            if (templateId) {
              saveContainerDirectEdits(container, templateId);
            }
            if (didDrag) options?.onChanged?.();
          }
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
      };

      el.addEventListener("mousedown", handleMouseDown);
      cleanupFns.push(() => el.removeEventListener("mousedown", handleMouseDown));
    }
  };

  // Setup every element already present.
  container.querySelectorAll<HTMLElement>(EDITABLE_SELECTOR).forEach(setupElement);

  // Setup elements added later (e.g. a table/column/row inserted through the
  // table toolbar) so the user can type fields into them right away.
  // Guard against environments without MutationObserver (e.g. some jsdom tests).
  if (typeof MutationObserver !== "undefined") {
    const observer = new MutationObserver((mutations) => {
      for (const mut of mutations) {
        for (const node of Array.from(mut.addedNodes)) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches(EDITABLE_SELECTOR)) setupElement(node);
          node
            .querySelectorAll?.(EDITABLE_SELECTOR)
            .forEach((el) => setupElement(el as HTMLElement));
        }
      }
    });
    observer.observe(container, { childList: true, subtree: true });
    cleanupFns.push(() => observer.disconnect());
  }

  return () => {
    cleanupFns.forEach((fn) => fn());
  };
}
